import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { DatePicker, InputWithLabel } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import ReactDatePicker from "react-datepicker";
import Toggles from "../../components/common/Toggles";
import { uploadImage } from "../../redux/product/slice";
import { toast } from "react-toastify";
import FileUploadChoose from "../../components/common/FileUploadChoose";
import { InputError } from "../../components/error/error";
import {
  createBannerSetting,
  getBannerSettingList,
} from "../../redux/masterSetting/slice";
import moment from "moment";
import RippleLoader from "../../components/loader/RippleLoader";

const AddEditBannerModal = ({
  onClose,
  selectedData,
  isType,
  search,
  pageCount,
  pageLimit,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isToggle, setIsToggle] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [isDisable, setIsDisable] = useState(true);

  const handleDateChange = (dates, setFieldValue) => {
    setDateRange(dates);

    if (dates[0] && dates[1]) {
      const formattedDateRange = `${moment(dates[0]).format(
        "DD/MM/YYYY"
      )} - ${moment(dates[1]).format("DD/MM/YYYY")}`;
      setFieldValue("date", formattedDateRange);
    } else {
      setFieldValue("date", "");
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);

    const formattedDateRange =
      isToggle && dateRange[0] && dateRange[1]
        ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
            dateRange[1]
          ).format("DD/MM/YYYY")}`
        : null;

    const payload = {
      title: values?.banner_title,
      is_schedule: isToggle,
      date_range: formattedDateRange,
      banner_image_ids: imageUrls,
    };
    const action = selectedData?.id
      ? createBannerSetting({ id: selectedData.id, ...payload })
      : createBannerSetting(payload);
    try {
      const res = await dispatch(action);
      if (res.type === "createBannerSetting/fulfilled") {
        dispatch(getBannerSettingList({ search, pageCount, pageLimit }));
      }
    } catch (error) {
      console.error("Error creating banner:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
      onClose();
    }
  };

  const handleEditClick = () => {
    setShowConfirmationModal(true);
  };

  // upload image
  const handleFileChange = async (event, setFieldValue) => {
    const file = event.target.files[0];
    if (!file) return;

    const newUploadedFile = { url: URL.createObjectURL(file) };
    setUploadedFiles([newUploadedFile]);

    try {
      const formData = new FormData();
      formData.append("file[]", file);
      formData.append("type", "banner_image");

      setIsLoading(true);

      const response = await dispatch(uploadImage(formData));

      if (
        response.type === "uploadImage/fulfilled" &&
        response.payload?.data?.length > 0
      ) {
        const uploadedImageId = response.payload.data[0].id;

        setImageUrls([uploadedImageId]);

        setFieldValue("uploadedFiles", [newUploadedFile]);

        toast.success("Image uploaded successfully!");
      } else {
        throw new Error("Image upload failed");
      }
    } catch (error) {
      toast.error("Error uploading image. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // get image date and status
  useEffect(() => {
    if (isType === "edit" && selectedData) {
      setIsToggle(selectedData?.is_schedule || false);
      setDateRange([
        selectedData?.start_date ? new Date(selectedData.start_date) : null,
        selectedData?.end_date ? new Date(selectedData.end_date) : null,
      ]);
      if (selectedData?.documents?.url) {
        setUploadedFiles([{ url: selectedData.documents.url }]);
        setImageUrls([selectedData.banner_image_id]);
      }
    }
  }, [isType, selectedData]);

  // delete image
  const handleDelete = (index, setFieldValue) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    setFieldValue("uploadedFiles", updatedFiles);
  };

  return (
    <>
      <div className="model" onClick={onClose}>
        <div
          className="bg-white items-center justify-center drop-shadow-lg rounded-lg w-full max-w-3xl py-8 px-6 mx-auto relative"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <h4 className="font-Montserrat text-3xl font-normal">
            {isType === "add" ? "Add New Banner" : "Edit Banner"}
          </h4>

          <Formik
            enableReinitialize={true}
            initialValues={{
              banner_title: selectedData?.title || "",
              banner_schedule: selectedData?.is_schedule || false,
              date:
                selectedData?.start_date && selectedData?.end_date
                  ? `${selectedData.start_date} - ${selectedData.end_date}`
                  : "",
              uploadedFiles: selectedData?.documents?.url
                ? [{ url: selectedData.documents.url }]
                : [],
            }}
            validationSchema={Yup.object().shape({
              banner_title: Yup.string()
                .trim()
                .min(3, "Title must be at least 3 characters")
                .required("Please enter banner title"),
              uploadedFiles: Yup.array()
                .min(1, "Please upload image")
                .required("Please upload image"),
              banner_schedule: Yup.boolean().required(
                "Please select whether the banner is scheduled"
              ),
              date: isToggle
                ? Yup.string().required("Please select a date range")
                : Yup.string(),
            })}
            onSubmit={(values) => {
              setFormValues(values);
              handleEditClick();
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setFieldValue,
            }) => (
              <Form>
                <div className="mb-4 mt-3">
                  <>
                    <InputWithLabel
                      className={`${
                        errors.banner_title && touched.banner_title
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out py-1`}
                      idFromName="banner_title"
                      label="Banner Title*"
                      type={"text"}
                      onChange={(e) => {
                        handleChange(e);
                        setIsDisable(false);
                      }}
                      onBlur={handleBlur}
                      value={values.banner_title}
                      errors={errors.banner_title && touched.banner_title}
                      errorsText={errors.banner_title}
                    />
                  </>
                </div>
                {/* Toggle Switch */}
                <div className="grid grid-cols-2 gap-6 mb-3">
                  <div className="flex items-center ">
                    <label className="flex items-center cursor-pointer">
                      <Toggles
                        checked={isToggle}
                        toggleFor={isToggle}
                        onChange={() => {
                          setIsToggle((prev) => !prev);
                          setIsDisable(false);
                          handleChange({
                            target: {
                              name: "banner_schedule",
                              value: !isToggle,
                            },
                          });
                        }}
                      />
                      <span className="ml-2 text-base font-Proxima">
                        {isToggle
                          ? "Switch if off if there’s no date range"
                          : "Switch it on if it’s a scheduled banner "}
                      </span>
                    </label>
                  </div>
                </div>
                {/* date range */}
                <div className="grid grid-cols-2 gap-6 mb-3">
                  {isToggle && (
                    <div className="relative">
                      <ReactDatePicker
                        selected={dateRange[0]}
                        onChange={(dates) => {
                          handleDateChange(dates, setFieldValue);
                          setIsDisable(false);
                        }}
                        selectsRange
                        isClearable={true}
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date range*"
                        className={`w-full border px-2 py-2 rounded ${
                          errors.date && touched.date
                            ? "border-red"
                            : "border-borderTheme/80"
                        }`}
                      />
                      {errors.date && touched.date && (
                        <InputError errorTitle={errors.date} />
                      )}
                    </div>
                  )}
                </div>
                {/* Product Image Upload */}
                <div className="mb-4 mt-4">
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Banner Image
                  </label>
                  {isLoading ? (
                    <RippleLoader />
                  ) : (
                    <>
                      <FileUploadChoose
                        className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                          errors.uploadedFiles && touched.uploadedFiles
                            ? "border-red"
                            : ""
                        }`}
                        handleFileChange={(event) =>
                          handleFileChange(event, setFieldValue)
                        }
                        uploadedFiles={uploadedFiles}
                        onDelete={(index) => handleDelete(index, setFieldValue)}
                      />
                      {errors.uploadedFiles && touched.uploadedFiles ? (
                        <InputError errorTitle={errors.uploadedFiles} />
                      ) : null}
                    </>
                  )}
                </div>
                {/* Submit button */}
                <div className="flex justify-end gap-3 mt-6 w-full">
                  <button
                    onClick={onClose}
                    type="button"
                    className=" text-themeBtn w-full flex items-center uppercase font-Montserrat justify-center px-6 py-1.5 rounded border border-themeBtn"
                  >
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={isDisable}
                    className={`${
                      isDisable
                        ? "bg-mGray cursor-not-allowed text-borderGrey"
                        : "bg-themeBtn  border-2 border-themeBtn  text-white"
                    }  flex items-center w-full justify-center px-6 py-1.5 rounded font-Montserrat uppercase `}
                  >
                    <span>Add</span>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        {showConfirmationModal && (
          <div>
            <ConfirmationModel
              confirmText={isType === "edit" ? "Edit" : "Add"}
              onClose={() => setShowConfirmationModal(false)}
              onConfirm={() => handleSubmit(formValues)}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AddEditBannerModal;
